import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { LogOut, Search, Download, Trash2, Copy, Check, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bbfit_admin_token";

const LABELS = {
  goal: {
    dimagrire: "Dimagrire",
    tonificare: "Tonificare",
    forza_massa: "Forza e massa",
    costanza: "Costanza",
    salute_energia: "Salute ed energia",
  },
  frequency: {
    "1_volta": "1 volta",
    "2_volte": "2 volte",
    "3_volte": "3 volte",
    piu_di_3: "Più di 3",
    non_mi_alleno: "Non si allena",
  },
  timing: {
    subito: "Subito",
    entro_un_mese: "Entro un mese",
    da_settembre: "Da settembre",
    raccolgo_info: "Solo info",
  },
  contact_method: {
    telefono: "Telefono",
    whatsapp: "WhatsApp",
    email: "Email",
  },
};

const prettify = (field, val) => LABELS[field]?.[val] || val;

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  return (
    <div className="min-h-screen bg-obsidian text-cream font-sans" data-testid="admin-page">
      {token ? (
        <Dashboard token={token} onLogout={logout} />
      ) : (
        <LoginForm
          onLogged={(t) => {
            localStorage.setItem(STORAGE_KEY, t);
            setToken(t);
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Login ---------------- */
function LoginForm({ onLogged }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      onLogged(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore di accesso.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="grain-overlay pointer-events-none" aria-hidden />
      <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
        <div className="mb-10">
          <div className="flex items-baseline gap-2 mb-6">
            <span className="font-serif text-3xl italic font-light text-cream">BB</span>
            <span className="font-sans text-xs uppercase tracking-[0.35em] text-cream/70">fit</span>
          </div>
          <div className="text-[0.68rem] uppercase tracking-[0.4em] text-gold mb-4">
            Area riservata
          </div>
          <h1 className="font-serif font-light text-cream text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
            <em className="italic">Accedi</em> alla dashboard.
          </h1>
          <p className="mt-5 text-cream/60 font-light text-sm">
            Consulta i lead qualificati generati dalla landing page.
          </p>
        </div>

        <label className="text-[0.62rem] uppercase tracking-[0.32em] text-cream/45">
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="editorial-input"
          placeholder="••••••••"
          data-testid="admin-password"
          autoFocus
          required
        />

        {error && (
          <div
            data-testid="admin-error"
            className="mt-6 border border-destructive/50 bg-destructive/10 text-cream/90 px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-10 w-full bg-gold hover:bg-gold-hover text-obsidian py-4 font-sans uppercase tracking-[0.24em] text-[0.75rem] font-semibold disabled:opacity-60 transition-colors duration-500"
        >
          {loading ? "Verifico…" : "Entra"}
        </button>

        <a
          href="/"
          className="mt-8 block text-center text-[0.65rem] uppercase tracking-[0.32em] text-cream/45 hover:text-gold transition-colors"
        >
          ← Torna al sito
        </a>
      </form>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | subito | today
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { "X-Admin-Token": token },
      });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        setError(err?.response?.data?.detail || "Errore nel caricare i lead.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = new Date();
    return leads.filter((l) => {
      if (
        q &&
        !`${l.full_name} ${l.email} ${l.phone}`.toLowerCase().includes(q)
      ) {
        return false;
      }
      if (filter === "subito" && l.answers?.timing !== "subito") return false;
      if (filter === "today") {
        const d = new Date(l.created_at);
        if (d.toDateString() !== now.toDateString()) return false;
      }
      return true;
    });
  }, [leads, query, filter]);

  const exportCSV = () => {
    const header = [
      "Data",
      "Nome",
      "Email",
      "Telefono",
      "Obiettivo",
      "Frequenza",
      "Inizio",
      "Contatto",
    ];
    const rows = filtered.map((l) => [
      new Date(l.created_at).toLocaleString("it-IT"),
      l.full_name,
      l.email,
      l.phone,
      prettify("goal", l.answers.goal),
      prettify("frequency", l.answers.frequency),
      prettify("timing", l.answers.timing),
      prettify("contact_method", l.answers.contact_method),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bbfit-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const removeLead = async (id) => {
    if (!window.confirm("Eliminare questo lead? L'operazione è irreversibile.")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setSelected(null);
    } catch (err) {
      alert(err?.response?.data?.detail || "Errore nell'eliminazione.");
    }
  };

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <header className="border-b border-cream/10 sticky top-0 bg-obsidian/90 backdrop-blur-md z-30">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-xl italic font-light text-cream">BB</span>
            <span className="font-sans text-[0.65rem] uppercase tracking-[0.35em] text-cream/70">
              fit — admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.3em] text-cream/70 hover:text-gold px-3 py-2 transition-colors"
              title="Aggiorna"
            >
              <RefreshCw size={13} strokeWidth={1.4} className={loading ? "animate-spin" : ""} />
              <span className="hidden md:inline">Aggiorna</span>
            </button>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.3em] text-cream/70 hover:text-gold px-3 py-2 transition-colors"
            >
              <LogOut size={13} strokeWidth={1.4} />
              <span className="hidden md:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header + stats */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[0.68rem] uppercase tracking-[0.4em] text-gold mb-3">
              Dashboard lead
            </div>
            <h1 className="font-serif font-light text-cream text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
              {leads.length} <em className="italic text-gold/95">lead</em> ricevuti.
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportCSV}
              disabled={filtered.length === 0}
              data-testid="admin-export-csv"
              className="flex items-center gap-2 border border-cream/25 hover:border-gold hover:text-gold px-5 py-3 text-[0.68rem] uppercase tracking-[0.28em] text-cream/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download size={13} strokeWidth={1.4} />
              Esporta CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-8">
          <div className="relative flex-1">
            <Search size={14} strokeWidth={1.4} className="absolute left-0 top-1/2 -translate-y-1/2 text-cream/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, email o telefono"
              className="editorial-input pl-6"
              data-testid="admin-search"
            />
          </div>

          <div className="flex items-center gap-1 border border-cream/10 p-1">
            {[
              { k: "all", label: "Tutti" },
              { k: "subito", label: "Subito" },
              { k: "today", label: "Oggi" },
            ].map((f) => (
              <button
                key={f.k}
                type="button"
                onClick={() => setFilter(f.k)}
                data-testid={`admin-filter-${f.k}`}
                className={`px-4 py-2 text-[0.62rem] uppercase tracking-[0.28em] transition-colors ${
                  filter === f.k
                    ? "bg-gold text-obsidian"
                    : "text-cream/70 hover:text-cream"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {error && (
          <div className="border border-destructive/50 bg-destructive/10 text-cream/90 px-4 py-3 text-sm mb-6">
            {error}
          </div>
        )}

        <div className="border border-cream/10" data-testid="admin-leads-table">
          {/* Head */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-cream/10 text-[0.6rem] uppercase tracking-[0.3em] text-cream/45 bg-onyx/40">
            <div className="col-span-2">Data</div>
            <div className="col-span-3">Nome</div>
            <div className="col-span-3">Contatti</div>
            <div className="col-span-2">Obiettivo</div>
            <div className="col-span-1">Inizio</div>
            <div className="col-span-1 text-right">Azioni</div>
          </div>

          {loading && leads.length === 0 ? (
            <div className="p-10 text-center text-cream/50 text-sm">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-cream/50 text-sm" data-testid="admin-empty-state">
              Nessun lead — appena qualcuno compilerà il quiz apparirà qui.
            </div>
          ) : (
            filtered.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                onOpen={() => setSelected(lead)}
                onDelete={() => removeLead(lead.id)}
              />
            ))
          )}
        </div>
      </main>

      {selected && (
        <LeadDrawer lead={selected} onClose={() => setSelected(null)} onDelete={() => removeLead(selected.id)} />
      )}
    </div>
  );
}

/* ---------------- Row ---------------- */
function LeadRow({ lead, onOpen, onDelete }) {
  const d = new Date(lead.created_at);
  const isSubito = lead.answers.timing === "subito";
  return (
    <div
      onClick={onOpen}
      data-testid={`lead-row-${lead.id}`}
      className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-cream/5 hover:bg-cream/[0.03] cursor-pointer transition-colors"
    >
      <div className="md:col-span-2 text-cream/60 text-sm">
        <div>{d.toLocaleDateString("it-IT")}</div>
        <div className="text-[0.65rem] uppercase tracking-[0.2em] text-cream/40">
          {d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      <div className="md:col-span-3">
        <div className="font-serif italic text-lg text-cream">{lead.full_name}</div>
        {isSubito && (
          <span className="inline-block mt-1 text-[0.55rem] uppercase tracking-[0.28em] text-gold border border-gold/40 px-2 py-0.5">
            Hot lead
          </span>
        )}
      </div>
      <div className="md:col-span-3 text-sm text-cream/70">
        <div>{lead.email}</div>
        <div className="text-cream/50">{lead.phone}</div>
      </div>
      <div className="md:col-span-2 text-sm text-cream/70">
        {prettify("goal", lead.answers.goal)}
        <div className="text-[0.62rem] uppercase tracking-[0.22em] text-cream/40 mt-1">
          {prettify("frequency", lead.answers.frequency)}
        </div>
      </div>
      <div className="md:col-span-1 text-sm text-cream/70">
        {prettify("timing", lead.answers.timing)}
      </div>
      <div className="md:col-span-1 flex md:justify-end items-start gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="text-cream/40 hover:text-destructive p-1 transition-colors"
          title="Elimina"
          data-testid={`lead-delete-${lead.id}`}
        >
          <Trash2 size={14} strokeWidth={1.4} />
        </button>
      </div>
    </div>
  );
}

/* ---------------- Drawer ---------------- */
function LeadDrawer({ lead, onClose, onDelete }) {
  const [copied, setCopied] = useState(null);
  const d = new Date(lead.created_at);
  const contactChannel = lead.answers.contact_method;

  const copy = (text, tag) => {
    navigator.clipboard.writeText(text);
    setCopied(tag);
    setTimeout(() => setCopied(null), 1400);
  };

  const contactHref =
    contactChannel === "whatsapp"
      ? `https://wa.me/${lead.phone.replace(/\D/g, "")}`
      : contactChannel === "telefono"
      ? `tel:${lead.phone}`
      : `mailto:${lead.email}`;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-obsidian/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="lead-drawer"
    >
      <div
        className="w-full md:w-[520px] h-full bg-onyx border-l border-cream/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 md:p-10">
          <div className="flex items-center justify-between mb-8">
            <span className="text-[0.65rem] uppercase tracking-[0.4em] text-gold">
              Dettaglio lead
            </span>
            <button
              type="button"
              onClick={onClose}
              className="text-cream/60 hover:text-cream text-xl leading-none"
              data-testid="drawer-close"
            >
              ×
            </button>
          </div>

          <h2 className="font-serif font-light text-cream text-4xl leading-tight mb-2">
            {lead.full_name}
          </h2>
          <div className="text-[0.65rem] uppercase tracking-[0.28em] text-cream/45 mb-8">
            Ricevuto il {d.toLocaleDateString("it-IT")} · {d.toLocaleTimeString("it-IT")}
          </div>

          {/* Quick contact */}
          <a
            href={contactHref}
            target={contactChannel === "email" ? "_self" : "_blank"}
            rel="noreferrer"
            className="block w-full text-center bg-gold hover:bg-gold-hover text-obsidian py-4 font-sans uppercase tracking-[0.24em] text-[0.72rem] font-semibold transition-colors mb-8"
            data-testid="drawer-quick-contact"
          >
            Contatta via {prettify("contact_method", contactChannel)}
          </a>

          {/* Fields */}
          <dl className="space-y-5">
            <DetailField
              label="Email"
              value={lead.email}
              onCopy={() => copy(lead.email, "email")}
              copied={copied === "email"}
            />
            <DetailField
              label="Telefono"
              value={lead.phone}
              onCopy={() => copy(lead.phone, "phone")}
              copied={copied === "phone"}
            />
            <div className="divider-thin my-2" />
            <DetailField label="Obiettivo" value={prettify("goal", lead.answers.goal)} />
            <DetailField label="Frequenza" value={prettify("frequency", lead.answers.frequency)} />
            <DetailField label="Quando inizia" value={prettify("timing", lead.answers.timing)} />
            <DetailField
              label="Canale preferito"
              value={prettify("contact_method", lead.answers.contact_method)}
            />
          </dl>

          <div className="mt-10 pt-6 border-t border-cream/10 flex justify-between items-center">
            <span className="text-[0.6rem] uppercase tracking-[0.28em] text-cream/40">
              ID: {lead.id.slice(0, 8)}
            </span>
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.28em] text-cream/50 hover:text-destructive transition-colors"
              data-testid="drawer-delete"
            >
              <Trash2 size={12} strokeWidth={1.4} />
              Elimina lead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value, onCopy, copied }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="text-[0.6rem] uppercase tracking-[0.32em] text-cream/40 mb-1">
          {label}
        </div>
        <div className="text-cream text-base font-light break-words">{value}</div>
      </div>
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="text-cream/40 hover:text-gold p-1 transition-colors"
          title="Copia"
        >
          {copied ? <Check size={14} strokeWidth={1.6} /> : <Copy size={14} strokeWidth={1.4} />}
        </button>
      )}
    </div>
  );
}
